import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import IconButton from "@mui/material/IconButton";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import {AddRounded, DeleteRounded, DownloadRounded, InfoRounded, PreviewRounded} from "@mui/icons-material";
import { Button, Stack, useTheme } from "@mui/material";
import { formatDate } from "../../../utils/FormatUtils";
import { getLocalGridState } from "../../../utils/GridUtils";
import { STATE_PRINTOUT_ACTIVE_FILTERS, STATE_PRINTOUT_TOTAL_COUNT, STATE_REPORT_UPDATING } from "../constants/IntermediariesRecoil";
import {ID_ERROR, ID_PROCESSING} from "../constants/IntermediariesStatuses";
import EmptyState from "../../../../../util/component/EmptyState";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { useNavigate } from "react-router-dom";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import { useIntermediariesPrintoutRepository } from "../../../api/repositories/IntermediariesPrintoutRepository";
import { formatAmount, getQuarterlyByKey } from "./ReportTableUtil";
import { useHasScope } from "../../../../../config/globalConfig";
import {
    SCOPE_FINANCE_CREATE_INTERMEDIARIES_PRINTOUTS,
    SCOPE_FINANCE_INTERMEDIARIES_DELETE_PRINTOUTS
} from "../constants/ConstantsAuth";
import {downloadPDF} from "./ReportTableUtil";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import FinanceDialog from "../../../component/FinanceDialog";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {decode} from "../../../utils/Base64Utils";
const PrintoutTable = () => {

    const UNIQUE_ID = "fnn-intermediaries-printout";

    const { t } = useTranslation('finance', { keyPrefix: 'intermediaries' });
    const { getPrintouts, downloadPrintoutHTML, downloadPrintoutPdf, deleteIntermediaryPrintout } = useIntermediariesPrintoutRepository();
    const navigate = useNavigate();
    const [updating, setUpdating] = useRecoilState(STATE_REPORT_UPDATING);
    const setTotalDataCount = useSetRecoilState(STATE_PRINTOUT_TOTAL_COUNT);
    const filterDefs = useRecoilValue(STATE_PRINTOUT_ACTIVE_FILTERS);
    const theme = useTheme();
    const gridRef = useRef<AgGridReact>(null);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [reportToDelete, setReportToDelete] = useState({ id: '', name: '' })
    const hasScope = useHasScope;
    const {  dispatchGlobalData, globalData: { user } } = useContext(GlobalContext);
   
    const legalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all");
    const dealers: any[] = user.dealers?.filter((x: any) => x.name.toLowerCase() !== "all");


    const columnDefs : ColDef[] = [
        {
            field: 'reportYear',
            headerName: t('tables.printout.year'),
            headerTooltip: t('tables.printout.year'),
            unSortIcon: true,
        },
        {
            field: 'reportQuarterly',
            headerName: t('tables.printout.quarterly'),
            headerTooltip: t('tables.printout.quarterly'),
            cellRenderer:(params: any) => getQuarterlyByKey(params.value),
            tooltipValueGetter:(params: any) => getQuarterlyByKey(params.value),
            unSortIcon: true,
        },
        {
            field: 'reportCreationDate',
            headerName: t('tables.printout.reportCreationDate'),
            headerTooltip: t('tables.printout.reportCreationDate'),
            unSortIcon: true,
            valueFormatter: params => formatDate(params.value),
            tooltipValueGetter: (params:any) => formatDate(params.value)

        },
        {
            field: 'intermediaryId',
            headerName: t('tables.printout.intermediary'),
            headerTooltip: t('tables.printout.intermediary'),
            tooltipValueGetter: (params:any) => params.value,
            unSortIcon: true,
        },
        {
            field: 'intermediaryName',
            headerName: t('tables.printout.intermediaryName'),
            headerTooltip: t('tables.printout.intermediaryName'),
            tooltipValueGetter: (params:any) => params.value,
            unSortIcon: true,
        },
        {
            field: 'totalReservation',
            headerName: t('tables.printout.totalReservation'),
            headerTooltip: t('tables.printout.totalReservation'),
            tooltipValueGetter: (params:any) => params.value,
            unSortIcon: true,
        },
        {
            field: 'totalCommissions',
            headerName: t('tables.printout.totCommissions'),
            headerTooltip: t('tables.printout.totCommissions'),
            cellRenderer: (params:any) => formatAmount(params.value, 0.01),
            tooltipValueGetter: (params:any) => formatAmount(params.value, 0.01),
            unSortIcon: true,
        },
        {
            field: 'commissionRate',
            headerName: t('tables.printout.percentage'),
            headerTooltip: t('tables.printout.percentage'),
            unSortIcon: true,
        },
        {
            field: 'taxWithholder',
            headerName: t('tables.printout.tk'),
            headerTooltip: t('tables.printout.tk'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'errorReason',
            headerName: t('tables.printout.errorReason'),
            headerTooltip: t('tables.printout.errorReason'),
            minWidth: 200,
            unSortIcon: true,
            cellRenderer: (params:any) => params?.value ? t("printout-wizard.snackbars.handleErrors." + params.value) : "",
            tooltipValueGetter: (params:any) => params?.value ? t("printout-wizard.snackbars.handleErrors." + params.value) : ""
        },
        {
            field: "legalEntity", headerName: t("tables.printout.legal-entity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: legalEntities?.length <= 1
        },
        {
            field: "dealer", headerName: t("tables.printout.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: dealers?.length <= 1
        },
        {
            field: '_actions',
            headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () =>
                        <FinanceDynamicActions>
                            <IconButton title={t('preview.buttons.preview')}  sx={{ color: theme.palette.accent.main }}
                                        disabled={params.data.status === ID_PROCESSING || params.data.status === ID_ERROR} onClick={() => {
                                downloadPrintoutHTML(params.data.reportId, params.data.intermediaryId).then((res) => {
                                    if (res?.result) {
                                        let pdfWindow = window.open("");
                                        pdfWindow!.document.write(decode(res.result.file));
                                    } else {
                                        dispatchGlobalData({
                                            type: setSnackbar,
                                            payload: {
                                                snackbar: {
                                                    message: res.message !== "" ? t("printout-wizard.snackbars.handleErrors." + res.message): t("printout-wizard.snackbars.handleErrors.errors.generic"),
                                                    type: snackbarTypes.error,
                                                    show: true,
                                                    autoClose: true
                                                }
                                            }
                                        });
                                    }
                                });
                            }}>
                                <PreviewRounded/>
                            </IconButton>
                            <IconButton title={t('preview.buttons.download')}  sx={{ color: theme.palette.accent.main }}
                                disabled={params.data.status === ID_PROCESSING || params.data.status === ID_ERROR } onClick={() => {
                                    downloadPrintoutPdf(params.data.reportId, params.data.intermediaryId).then((res) => {
                                        if (res?.result && res?.result?.file) {
                                            downloadPDF(res?.result?.file, res?.result?.name);
                                        } else {
                                            dispatchGlobalData({
                                                type: setSnackbar,
                                                payload: {
                                                    snackbar: {
                                                        message: res.message !== "" ? t("printout-wizard.snackbars.handleErrors." + res.message): t("printout-wizard.snackbars.handleErrors.errors.generic"),
                                                        type: snackbarTypes.error,
                                                        show: true,
                                                        autoClose: true
                                                    }
                                                }
                                            });
                                        }
                                    });
                                }}>
                                <DownloadRounded />
                            </IconButton>
                            {hasScope(SCOPE_FINANCE_INTERMEDIARIES_DELETE_PRINTOUTS) &&
                                <IconButton title={t('preview.buttons.delete')} sx={{ color: theme.palette.accent.main }}
                                            onClick={() => {
                                                setOpenDeleteModal(true);
                                                setReportToDelete({ id: params.data.id, name: getQuarterlyByKey(params.data.reportQuarterly) + " " +params.data.intermediaryName });
                                            }}>
                                    <DeleteRounded/>
                                </IconButton>
                            }
                        </FinanceDynamicActions>
                };
            },
            suppressFiltersToolPanel: true
        }
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {
            title: t('tables.printout.noRows.title'),
            content: t('tables.printout.noRows.content')
        }
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
    }, [filterDefs])

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
        }
    }, [updating])


    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                getPrintouts(startRow, endRow - startRow, sort, order, filterDefs, null)
                    .then(response => {
                        let result = response.result;
                        setTotalDataCount(result.total);
                        if (result.total === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result.total
                        })
                    })
                    .catch(() => params.fail());
            }
        };
    }


    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderActions: () => (
            <Stack>
               {hasScope(SCOPE_FINANCE_CREATE_INTERMEDIARIES_PRINTOUTS) && <Button fullWidth color={"accent"} variant="contained" component="label" onClick={() => {
                    navigate("/finance/intermediaries/printouts-wizard")
                }} startIcon={<AddRounded/>}>{t('buttons.createPrintout')}</Button> }
            </Stack>
        )
    }

    return (
        <>
            <AgGridCustomTable
                toolbar={toolbar}
                rowModelType='serverSide'
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows}
                serverSideInfiniteScroll
                gridRef={gridRef}
                columnDefs={columnDefs}
                gridOptions={gridOptions}
                noRowsOverlayComponent={noRowsOverlayComponent}
                suppressRowClickSelection
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));
                }}
            />
            <FinanceDialog
                title={t('preview.buttons.deleteTitle')}
                content={t('preview.buttons.deleteContent', {reportName: reportToDelete.name})}
                cancelButton={t('preview.buttons.deleteCancel')}
                callback={() => {
                    deleteIntermediaryPrintout(reportToDelete.id).then((res) => {
                        if (res.status === 'OK') {
                            dispatchGlobalData({
                                type: setSnackbar,
                                payload: {
                                    snackbar: {
                                        message: t('printout-wizard.snackbars.deleteSuccessPrintout'),
                                        type: snackbarTypes.success,
                                        show: true,
                                        autoClose: true
                                    }
                                }
                            });
                            setUpdating(true);
                        } else {
                            dispatchGlobalData({
                                type: setSnackbar,
                                payload: {
                                    snackbar: {
                                        message: t('printout-wizard.snackbars.deleteErrorPrintout'),
                                        type: snackbarTypes.error,
                                        show: true,
                                        autoClose: true
                                    }
                                }
                            });
                        }
                    })
                }}
                confirmButton={t('preview.buttons.deleteConfirm')}
                open={openDeleteModal}
                onClose={() => {
                    setOpenDeleteModal(false)
                }}
                titleIcon={<InfoRounded sx={{color: 'accent.main'}}/>}
            />
        </>
    );
}

export default PrintoutTable;